<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { getCurrentLocale } from '@/i18n';
import { HOTEL_ONLY, PACKAGE, CRUISE, AIR_ONLY, LANG_ENGLISH, CREATED, DEPARTURE_DATE, TOUR } from '@/constants';
import Button from 'primevue/button';
import Checkbox from 'primevue/checkbox';
import DatePicker from 'primevue/datepicker';
import InputText from 'primevue/inputtext';
import MultiSelect from 'primevue/multiselect';
import Select from 'primevue/select';
import FilterNoteStatic from '@/components/common/FilterNoteStatic.vue';

const props = defineProps<{
  dataFilter: any;
  groupTypeList: object[];
  isShowQuoteId?: boolean;
  isShowArchiveFilter?: boolean;
  statisticNoteTotalPublic?: any;
}>();

const emits = defineEmits<{
  handleFilterData: [dataFilter: any];
  handleFilterTotal: [dataFilter: any];
  applyNewValue: [noteData: any];
}>();

const { t } = useI18n({ useScope: 'global' });
const isExpand = ref(false);
const currentLanguage = getCurrentLocale();

const dateOption = ref([
  {
    label: t('common.labels.quote_date'),
    value: CREATED
  },
  {
    label: t('common.labels.departure_date'),
    value: DEPARTURE_DATE
  }
]);

const quoteTypeList = ref([
  {
    label: t(`common.labels.${PACKAGE.toLowerCase()}`),
    value: PACKAGE
  },
  {
    label: t(`common.labels.${HOTEL_ONLY.toLowerCase()}`),
    value: HOTEL_ONLY
  },
  {
    label: t(`common.labels.${AIR_ONLY.toLowerCase()}`),
    value: AIR_ONLY
  },
  {
    label: t(`common.labels.${CRUISE.toLowerCase()}`),
    value: CRUISE
  },
  {
    label: t(`common.labels.tour`),
    value: TOUR
  }
]);

const dataFilter = computed(() => props.dataFilter);

const handleExpand = (status: boolean) => {
  isExpand.value = !status;
};

const handleApplyFilter = (dataFilter: any) => {
  emits('handleFilterData', dataFilter);
};

const handleResetFilter = () => {
  emits('handleFilterData', undefined);
};

const changeDate = (event: any) => {
  dataFilter.value.fromDate = event[0];
  dataFilter.value.toDate = event[1];
};

const handleTextToUpperCase = (text: string) => {
  return text.toUpperCase();
};

const updateModelValue = (event: any) => {
  if (event.target.value) {
    dataFilter.value.quoteId = event.target.value.trim();
  }
};

const handleFilterTotal = (event: any) => {
  emits('handleFilterTotal', event);
};

const applyNewValue = (event: any) => {
  emits('applyNewValue', event);
};
</script>

<template>
  <div class="filter-table">
    <div class="header">
      <div>
        <Button v-if="isExpand" @click="handleExpand(isExpand)" :label="$t('common.filter')" icon="pi pi-chevron-down" class="p-button-text button-action"></Button>
        <Button v-if="!isExpand" @click="handleExpand(isExpand)" :label="$t('common.filter')" icon="pi pi-chevron-right" class="p-button-text button-action"></Button>
      </div>
    </div>
    <div class="filter-wrapper" v-if="isExpand">
      <div class="filter-item">
        <p class="mb-3 ml-2 font-bold">{{ $t('common.labels.type_of_search') }}</p>
        <Select class="table-select w-full" :options="dateOption" optionLabel="label" optionValue="value" v-model="dataFilter.typeOfSearch">
          <template #option="slotProps">
            <p class="select-option">
              {{ slotProps.option.label }}
            </p>
          </template>
        </Select>
      </div>
      <div class="filter-item w-2">
        <p class="mb-3 ml-2 font-bold">{{ $t('common.labels.date_range') }}</p>
        <DatePicker @update:modelValue="changeDate($event)" :modelValue="[dataFilter.fromDate, dataFilter.toDate]" selectionMode="range" dateFormat="dd-M-yy" :selectOtherMonths="true" :placeholder="$t('common.date_format_range')"></DatePicker>
      </div>
      <div class="filter-item">
        <p class="mb-3 ml-2 font-bold">{{ $t('common.labels.quote_type') }}</p>
        <MultiSelect display="chip" v-model="dataFilter.quoteType" :options="quoteTypeList" optionLabel="label" optionValue="value" :placeholder="$t('common.labels.select_quote_type')" class="w-full w-20rem" :maxSelectedLabels="3">
          <template #option="slotProps">
            <p>
              {{ slotProps.option.label }}
            </p>
          </template>
        </MultiSelect>
      </div>
      <div class="filter-item">
        <p class="mb-3 ml-2 font-bold">{{ $t('common.labels.group_type') }}</p>
        <MultiSelect
          display="chip"
          v-model="dataFilter.groupType"
          :options="groupTypeList"
          :optionLabel="currentLanguage === LANG_ENGLISH ? 'name' : 'nameFr'"
          optionValue="sk"
          :placeholder="$t('advance_quote.form.select_group_type')"
          class="w-full w-20rem"
          :maxSelectedLabels="3"
        >
          <template #option="slotProps">
            <div class="max-w-15rem overflow-hidden text-overflow-ellipsis" v-tooltip.left="currentLanguage === LANG_ENGLISH ? slotProps.option['name'] : slotProps.option['nameFr']">
              {{ currentLanguage === LANG_ENGLISH ? slotProps.option['name'] : slotProps.option['nameFr'] }}
            </div>
          </template>
        </MultiSelect>
      </div>
      <div class="filter-item" v-if="isShowQuoteId">
        <p class="mb-3 ml-2 font-bold">{{ handleTextToUpperCase($t('worksheet.quote_id')) }}</p>
        <InputText v-model="dataFilter.quoteId" class="quote-filter" @change="updateModelValue($event)" />
      </div>
      <div class="archived" v-if="isShowArchiveFilter">
        <div>
          <Checkbox inputId="archived-id" v-model="dataFilter.showArchivedOnly" :binary="true" />
          <label class="ml-1" for="archived-id">{{ $t('common.labels.show_archived_quotes_only') }}</label>
        </div>
      </div>
    </div>
    <div class="p-3" v-if="isExpand && statisticNoteTotalPublic">
      <FilterNoteStatic :statisticNoteTotalPublic="statisticNoteTotalPublic" @handleFilterTotal="handleFilterTotal($event)" @applyNewValue="applyNewValue($event)"></FilterNoteStatic>
    </div>
    <div class="flex gap-3 ml-4" v-if="isExpand">
      <Button data-testid="filter-run-btn" :label="$t('common.labels.run')" class="mt-2 bg-primary" @click="handleApplyFilter(dataFilter)"></Button>
      <Button data-testid="filter-reset-btn" :label="$t('common.labels.reset')" class="mt-2 bg-primary" @click="handleResetFilter()"></Button>
    </div>
  </div>
</template>
